/** Student matching form section. */
import * as React from 'react';
import { Users } from 'lucide-react';
import { RadioGroup } from '@/components/forms/RadioGroup';
import { Select } from '@/components/forms/Select';
import { Textarea } from '@/components/forms/Textarea';
import { AvailabilityGrid } from '@/components/forms/AvailabilityGrid';
import { ProgramCascadeFields } from '@/components/common/ProgramCascadeFields';
import { TogglePill } from '@/components/forms/TogglePill';
import {
  BOOLEAN_SELECT_OPTIONS,
  REMOTE_SUBJECT_OPTIONS,
} from '@/components/students/student-configs';
import { collectProgramAllowedSlots } from '@/components/students/inperson/student-form-utils';
import { useTheme } from '@/hooks/useTheme';

/** Props for {@link StudentMatchingSection}. */
type StudentMatchingSectionProps = Readonly<{
  formData: Record<string, any>;
  onChange: (key: string, value: any) => void;
  programs: any[];
  subjectOptions: typeof REMOTE_SUBJECT_OPTIONS;
  partnerOptions: Array<{ value: string; label: string }>;
  sportClubOptions: Array<{ value: string; label: string }>;
  partnersError?: string;
  sportClubsError?: string;
  hasSportProgram: boolean;
}>;

/** Student matching form section. */
export function StudentMatchingSection({
  formData,
  onChange,
  programs,
  subjectOptions,
  partnerOptions,
  sportClubOptions,
  partnersError,
  sportClubsError,
  hasSportProgram,
}: StudentMatchingSectionProps) {
  const { companyName } = useTheme();
  const studentAllowedSlots = React.useMemo(
    () => collectProgramAllowedSlots(formData.programs, programs),
    [formData.programs, programs],
  );

  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Users className="w-4 h-4" />
        Disponibilité et Matching
      </h4>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="col-span-2">
          <ProgramCascadeFields
            programs={programs}
            value={{
              coursePath: formData.coursePath,
              programs: formData.programs,
            }}
            onChange={onChange}
            showTutoringType={false}
          />
        </div>
        <fieldset className="col-span-2 border-0 p-0 m-0 min-w-0">
          <legend className="text-sm font-medium text-foreground mb-2 block">
            Disponibilités hebdomadaires <span className="text-destructive">*</span>
          </legend>
          <AvailabilityGrid
            value={{ weekly: formData.availability ?? [] }}
            onChange={(value) => onChange('availability', value.weekly)}
            allowedSlots={studentAllowedSlots}
          />
        </fieldset>
        <div className="sm:col-span-2 grid grid-cols-1 gap-4 sm:grid-cols-2 items-end">
          <Select
            label="Matière"
            value={formData.subject ?? ''}
            onChange={(val) => onChange('subject', val)}
            options={subjectOptions}
            required
            size="sm"
            placeholder="Sélectionner une matière"
          />

          <fieldset className="border-0 p-0 m-0 min-w-0">
            <legend className="form-label">Disponibilités spécifiques</legend>
            <div className="flex gap-2 mt-2">
              <TogglePill
                label="Vacances scolaires"
                active={!!formData.availableOnVacations}
                onClick={() => onChange('availableOnVacations', !formData.availableOnVacations)}
                size="sm"
                className="flex-1 text-center"
              />
              <TogglePill
                label="Week-end"
                active={!!formData.availableOnWeekends}
                onClick={() => onChange('availableOnWeekends', !formData.availableOnWeekends)}
                size="sm"
                className="flex-1 text-center"
              />
              <TogglePill
                label="Jours fériés"
                active={!!formData.availableOnHolidays}
                onClick={() => onChange('availableOnHolidays', !formData.availableOnHolidays)}
                size="sm"
                className="flex-1 text-center"
              />
            </div>
          </fieldset>
        </div>

        <RadioGroup
          name="isPartner"
          label={
            companyName
              ? `Est engagé(e) avec un partenaire de ${companyName} ?`
              : 'Est engagé(e) avec un partenaire ?'
          }
          required
          layout="horizontal"
          options={BOOLEAN_SELECT_OPTIONS}
          value={
            formData.isPartner == null || formData.isPartner === ''
              ? undefined
              : String(formData.isPartner)
          }
          onValueChange={(v) => onChange('isPartner', v)}
        />

        {(formData.isPartner === 'true' || formData.isPartner === true) && (
          <Select
            label="Sélectionner le partenaire"
            value={formData.partnerName ?? ''}
            onChange={(val) => onChange('partnerName', val)}
            options={partnerOptions}
            required
            size="sm"
            placeholder="Chargement..."
            error={partnersError}
          />
        )}

        {hasSportProgram && (
          <div className="sm:col-span-2">
            <Select
              label="Club de sport"
              value={formData.clubDeSport ?? ''}
              onChange={(val) => onChange('clubDeSport', val)}
              options={sportClubOptions}
              required
              size="sm"
              placeholder="Sélectionner un Club de sport"
              error={sportClubsError}
            />
          </div>
        )}

        <div className="sm:col-span-2">
          <Textarea
            label="Commentaires"
            value={formData.comments ?? ''}
            onChange={(e) => onChange('comments', e.target.value)}
            rows={3}
            placeholder="Informations complémentaires sur l'élève"
          />
        </div>
      </div>
    </div>
  );
}
